import type { OverviewDigestViewModel } from './overviewDigest.js';
import type { FullChartSynthesisViewModel } from './fullChartSynthesis.js';
import type { TopicSynthesisFacet, TopicSynthesisViewModel } from './topicSynthesis.js';

export interface OverviewReaderDepthItem {
  topic:string; topicLabel:string; claimKey:string; facetLabels:readonly string[];
  coreReading:string; practicalApplication:string; verificationPrompt:string;
}
export interface OverviewReaderDepthViewModel {
  model:'OVERVIEW-READER-DEPTH-0.2.0'; status:'available'|'insufficient_evidence';
  items:readonly OverviewReaderDepthItem[]; crossTopicReading?:string; emptyState:string;
}

const TOPIC_LABEL:Readonly<Record<string,string>>=Object.freeze({
  personality:'個性',career:'事業',wealth:'財運',relationship:'感情',social:'人際',family:'家庭',home:'居住與家庭空間',wellbeing:'休息與內在節奏',
});
function stateText(facet:TopicSynthesisFacet):string {
  if(facet.balanceState==='mixed') return '同時有順手條件與需要調整的牽制';
  if(facet.balanceState==='counter_only') return '目前更需要看情境並保留調整空間';
  return '目前較有一致、可直接運用的方向';
}
function concisePreview(text:string):string {
  const match=text.match(/^.*?[。！？；]/u);
  return match?.[0] ?? text;
}
function itemFor(topic:string,claimKey:string,synthesis:TopicSynthesisViewModel|undefined):OverviewReaderDepthItem|undefined {
  if(!synthesis||synthesis.status!=='available'||!synthesis.facets.length) return undefined;
  const facets=synthesis.facets.slice(0,2); const primary=facets[0]!;
  const labels=Object.freeze(facets.map((facet)=>facet.label));
  const second=facets[1]?`，同時要把「${facets[1].label}」一起看` : '';
  return Object.freeze({topic,topicLabel:TOPIC_LABEL[topic]??topic,claimKey,facetLabels:labels,
    coreReading:`這一面向先看「${primary.label}」：${stateText(primary)}${second}。`,
    practicalApplication:concisePreview(primary.practicalApplication),
    verificationPrompt:primary.scenarioExamples[0]??`觀察「${primary.label}」是否在不同生活情境反覆出現。`,
  });
}
export function buildOverviewReaderDepth(
  digest:OverviewDigestViewModel,
  syntheses:readonly TopicSynthesisViewModel[],
  fullChart:FullChartSynthesisViewModel,
  limit=3,
):OverviewReaderDepthViewModel {
  if(digest.status!=='available') return Object.freeze({model:'OVERVIEW-READER-DEPTH-0.2.0',status:'insufficient_evidence',items:Object.freeze([]),emptyState:digest.emptyState??'目前可用的正式判讀還不足以整理成白話總覽。'});
  const map=new Map(syntheses.map((item)=>[item.topic,item] as const));
  const items=Object.freeze(digest.items.flatMap((item)=>{
    const built=itemFor(item.topic,item.claimKey,map.get(item.topic));
    return built?[built]:[];
  }).filter((item,index,all)=>all.findIndex((candidate)=>candidate.topic===item.topic)===index).slice(0,limit));
  if(!items.length) return Object.freeze({model:'OVERVIEW-READER-DEPTH-0.2.0',status:'insufficient_evidence',items,emptyState:'目前可用的正式判讀還不足以整理成白話總覽。'});
  const selectedTopics=new Set(items.map((item)=>item.topic));
  const interaction=fullChart.interactions.find((item)=>item.topics.every((topic)=>selectedTopics.has(topic)));
  const crossTopicReading=interaction
    ? `整體再把「${interaction.facetLabels.join('」和「')}」放在同一段生活流程裡核對；重點是看兩邊是否同時出現，而不是把它們解讀成彼此造成的結果。`
    : undefined;
  return Object.freeze({model:'OVERVIEW-READER-DEPTH-0.2.0',status:'available',items,...(crossTopicReading?{crossTopicReading}:{}),emptyState:''});
}
